import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format/datetime";

import type { ActivityFilters } from "./build-activity-entries";
import { ACTIVITY_CASE_LIMIT, useActivityLog } from "./use-activity-log";

/**
 * 大項目7「操作履歴」（管理）。誰がいつどの操作をしたかを案件横断で見る。
 *
 * #24 の「監査ログ」の代わりとして、既存の API で作れる範囲に絞った画面。
 * 監査ログ（改ざん耐性・エージェントの帰属・業務データの前後状態）ではないので
 * 名前を分けている。満たせない点は画面にも明記する。
 */
export function ActivityLogPage() {
  const { t } = useTranslation(["common", "domain"]);
  const [filters, setFilters] = useState<ActivityFilters>({});
  const { entries, isPending, isError, failedCaseCount } =
    useActivityLog(filters);

  const update = (patch: Partial<ActivityFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">
          {t("common:activityLog.title")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("common:activityLog.description")}
        </p>
      </header>

      <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
        {t("common:activityLog.scopeNote", { count: ACTIVITY_CASE_LIMIT })}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("common:activityLog.filterActor")}>
          <input
            type="text"
            value={filters.actor ?? ""}
            onChange={(event) => update({ actor: event.target.value })}
            className="border-border bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm focus-visible:ring-2 focus-visible:outline-none"
          />
        </Field>
        <Field label={t("common:activityLog.filterFrom")}>
          <input
            type="date"
            value={filters.from ?? ""}
            onChange={(event) => update({ from: event.target.value })}
            className="border-border bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm focus-visible:ring-2 focus-visible:outline-none"
          />
        </Field>
        <Field label={t("common:activityLog.filterTo")}>
          <input
            type="date"
            value={filters.to ?? ""}
            onChange={(event) => update({ to: event.target.value })}
            className="border-border bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm focus-visible:ring-2 focus-visible:outline-none"
          />
        </Field>
      </div>

      {failedCaseCount > 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("common:activityLog.partial", { count: failedCaseCount })}
        </p>
      ) : null}

      {isError ? (
        <p className="text-destructive text-sm">{t("common:state.error")}</p>
      ) : null}

      {isPending ? <Skeleton className="h-40 w-full" /> : null}

      {!isPending && !isError ? (
        entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("common:activityLog.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-muted/50 text-muted-foreground text-left text-xs">
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("common:activityLog.occurredAt")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("common:activityLog.actor")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("domain:caseNumber")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("domain:applicationType")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("common:activityLog.step")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={`${entry.caseId}-${entry.id}`}
                    className="border-border border-b last:border-0"
                  >
                    <td className="text-muted-foreground px-3 py-2 tabular-nums">
                      {formatDateTime(entry.occurredAt)}
                    </td>
                    <td className="px-3 py-2">{entry.actor}</td>
                    <td className="text-muted-foreground px-3 py-2 tabular-nums">
                      {entry.caseNumber}
                    </td>
                    <td className="px-3 py-2">
                      {entry.processModelDisplayName}
                    </td>
                    <td className="px-3 py-2">{entry.taskName ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
